import { baseApi } from 'common/store/baseApi';
import {
  categoryUrl, hideCategoryUrl, showCategoryUrl,
} from 'common/utils/network/endpoints';
import { applyCategoryDisplayOverrides } from 'common/utils/categoryDisplay';
import { Category } from 'common/type';

type CategoryListResponse = { status: boolean; message: string; data: Category[] };
type VisibilityBody = { category_id: number; category_type: 'system' | 'custom' };
type VisibilityResponse = { status: boolean; message: string };

export const ManageCategoriesAPI = baseApi
  .enhanceEndpoints({ addTagTypes: ['ManageCategories', 'Categories'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      // Full list (system + custom) WITH is_hidden flags + doc counts.
      // No exclude_hidden — the Manage screen must show hidden ones to unhide.
      // include_shared=1 additionally pulls in shared-in custom categories
      // (is_shared_in: true, sharer_names), rendered in their own section.
      // The screen updates this cache optimistically and debounces the writes,
      // so this is NOT invalidated on a successful toggle (re-fetching the list
      // you're toggling is what made it feel laggy). It's only re-fetched if a
      // write fails (to reconcile to server truth) or on a fresh mount.
      manageCategoriesList: builder.query<CategoryListResponse, void>({
        query: () => ({ url: `${categoryUrl}?include_shared=1`, method: 'GET' }),
        transformResponse: (resp: CategoryListResponse) => (
          resp?.data
            ? { ...resp, data: resp.data.map(applyCategoryDisplayOverrides) }
            : resp
        ),
        providesTags: ['ManageCategories'],
      }),
      // Plain writes. 'Categories' refreshes the upload picker; the dashboard
      // refreshes on its own remount. The Manage screen handles its own instant
      // optimistic update + debounce, so these don't invalidate 'ManageCategories'.
      hideCategory: builder.mutation<VisibilityResponse, VisibilityBody>({
        query: (body) => ({ url: hideCategoryUrl, method: 'POST', body }),
        invalidatesTags: ['Categories'],
      }),
      showCategory: builder.mutation<VisibilityResponse, VisibilityBody>({
        query: (body) => ({ url: showCategoryUrl, method: 'POST', body }),
        invalidatesTags: ['Categories'],
      }),
    }),
  });

export const {
  useManageCategoriesListQuery,
  useHideCategoryMutation,
  useShowCategoryMutation,
} = ManageCategoriesAPI;
